import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
});

// attach access token from localStorage as a fallback for cross-site cookie blocking
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mr_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// On 401 token expiry, attempt ONE silent refresh via the cookie-backed
// /auth/refresh endpoint. If that succeeds, retry the original request
// transparently. If it doesn't, bounce to /admin/login with a return-to
// URL so the user finishes where they left off.
let _refreshInFlight = null;
let _redirectingToLogin = false;
api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    const cfg = err?.config || {};
    const looksLikeAuthExpiry = status === 401 && /token|expired|unauthor|not authenticated/i.test(String(detail));
    const path = (typeof window !== "undefined" && window.location.pathname) || "";

    if (looksLikeAuthExpiry && !cfg._retried && path.startsWith("/admin") && !path.startsWith("/admin/login")) {
      cfg._retried = true;
      try {
        if (!_refreshInFlight) {
          _refreshInFlight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .then((r) => {
              const tok = r?.data?.access_token;
              if (tok) {
                localStorage.setItem("mr_token", tok);
                return tok;
              }
              throw new Error("no token in refresh response");
            })
            .finally(() => { _refreshInFlight = null; });
        }
        const newTok = await _refreshInFlight;
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newTok}`;
        return api(cfg);
      } catch {
        // refresh failed — fall through to redirect.
      }

      if (!_redirectingToLogin) {
        _redirectingToLogin = true;
        try {
          localStorage.removeItem("mr_token");
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/admin/login?session=expired&next=${returnTo}`);
        } catch { /* ignore */ }
      }
    }
    return Promise.reject(err);
  },
);

export function setToken(token) {
  if (token) localStorage.setItem("mr_token", token);
  else localStorage.removeItem("mr_token");
}

/**
 * Extract a human-readable error message from any axios error.
 * Critical: FastAPI's 422 returns `detail` as an Array<{msg, loc, ...}> —
 * rendering that array directly in JSX crashes React. Always pipe API
 * errors through this helper before putting them in a toast or state.
 */
export function extractErrMsg(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const parts = d.map((e) => (e && typeof e === "object" ? (e.msg || e.message || JSON.stringify(e)) : String(e)));
    return parts.filter(Boolean).join(", ") || fallback;
  }
  if (typeof d === "object") return d.msg || d.message || d.detail || fallback;
  return String(d) || fallback;
}
